import type { MouseEvent } from 'react'
import { SimulationCopyBasis, type SimulationCopyMade } from '../lib/simulationCopy.ts'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'
import { Tooltip } from './Tooltip.tsx'

/**
 * The super admin's way to run a conferência again (#586).
 *
 * A conferência that went to production cannot be re-run. This copies it into
 * a simulation in the internal account, analysed against what the ERP answered
 * when the original was. Once a copy exists the button becomes the way to it:
 * the simulation is in another account, so opening it is a link that switches.
 */
export interface CopyToSimulationControlProps {
  readonly copying: boolean
  /** The copy made on this screen, once there is one. */
  readonly made: SimulationCopyMade | undefined
  /** The copy's address, for the link it becomes. */
  readonly madeHref: string | undefined
  readonly onCopy: () => void
  readonly onOpenCopy: (event: MouseEvent<HTMLAnchorElement>) => void
}

export function CopyToSimulationControl({
  copying,
  made,
  madeHref,
  onCopy,
  onOpenCopy,
}: CopyToSimulationControlProps) {
  if (made !== undefined && madeHref !== undefined) {
    return (
      <Tooltip text={madeText(made)}>
        <a
          className="rounded-base border-stroke-brand-strong text-text-brand text-tSmall inline-flex h-8 items-center border px-2 font-semibold"
          href={madeHref}
          target="_blank"
          rel="noreferrer"
          onClick={onOpenCopy}
        >
          Abrir a simulação
        </a>
      </Tooltip>
    )
  }

  return (
    <Tooltip
      text={
        copying
          ? 'Copiando para uma simulação…'
          : 'Copiar para simulação — cria uma simulação na conta interna com o que o ERP respondeu quando esta conferência foi analisada, para rodar de novo quantas vezes for preciso. Nada muda nesta conferência nem no ERP.'
      }
    >
      <Button
        variant="secondary"
        tone="neutral"
        size="icon"
        leadingIcon={copying ? icons.busy : icons.simulation}
        aria-label="Copiar para simulação"
        disabled={copying}
        onClick={onCopy}
      />
    </Tooltip>
  )
}

function madeText(made: SimulationCopyMade): string {
  const basis =
    made.basis === SimulationCopyBasis.CurrentErp
      ? ' Tirada do estado atual do ERP: esta conferência foi analisada antes de guardarmos as respostas dele.'
      : ''
  const missing =
    made.missingAttachments === 0
      ? ''
      : made.missingAttachments === 1
        ? ' 1 receita não pôde ser copiada.'
        : ` ${made.missingAttachments} receitas não puderam ser copiadas.`
  return `Simulação criada na conta interna.${basis}${missing}`
}
